/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    savedItemsCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // COMMENTED OUT: entry point to provider signup / listing creation ("Apply to Partner" / "Post a new listing")
  // const createListingsLinkMaybe = showCreateListingsLink ? (
  //   <NamedLink className={css.createNewListingLink} name="NewListingPage">
  //     <FormattedMessage
  //       id={isAuthenticated ? "TopbarMobileMenu.newListingLink" : "TopbarMobileMenu.startSelling"}
  //     />
  //   </NamedLink>
  // ) : null;
  const createListingsLinkMaybe = null;

  if (!isAuthenticated) {
    return (
      <nav className={css.root}>
        <div className={css.content}>
          {/* Hero Section */}
          <div className={css.heroSection}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="TopbarMobileMenu.heroTitle" />
            </h1>
            <p className={css.heroSubtitle}>
              <FormattedMessage id="TopbarMobileMenu.heroSubtitle" />
            </p>
          </div>

          {/* Authentication Section - Simple */}
          <div className={css.authenticationSection}>
            <NamedLink name="SignupPage" className={css.signupButton}>
              <FormattedMessage id="TopbarMobileMenu.createAccount" />
            </NamedLink>
            <div className={css.loginWrapper}>
              <span className={css.alreadyMember}>
                <FormattedMessage id="TopbarMobileMenu.alreadyMember" />
              </span>{' '}
              <NamedLink name="LoginPage" className={css.loginLink}>
                <FormattedMessage id="TopbarMobileMenu.loginLink" />
              </NamedLink>
            </div>
          </div>

          {/* Discover Section */}
          <div className={css.sectionHeader}>
            <FormattedMessage id="TopbarMobileMenu.browseHeader" />
          </div>
          <div className={css.navigationSection}>
            <NamedLink name="BrandsPage" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.allBrandsLink" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </NamedLink>
            <NamedLink name="CategoriesPage" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.allCategoriesLink" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </NamedLink>
            <Link to="/categories/Baby-Kids" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.babyKidsLink" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </Link>
            <Link to="/categories/Fashion" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.fashionLink" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </Link>
            <Link to="/categories/Home-Kitchen" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.homeKitchenLink" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </Link>
            <Link to="/categories/Jewelry-Accessories" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.jewelryLink" defaultMessage="Jewelry & Accessories" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </Link>
            <Link to="/categories/Beauty-Wellness" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.beautyLink" defaultMessage="Beauty & Wellness" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </Link>
            <NamedLink name="GiftsPage" className={css.navigationLink}>
              <span className={css.navigationLinkContent}>
                <FormattedMessage id="TopbarMobileMenu.giftsLink" defaultMessage="Gifts" />
                <span className={css.navigationArrow}>→</span>
              </span>
            </NamedLink>
            {savedItemsCount > 0 ? (
              <NamedLink
                name="SavedPage"
                className={css.navigationLink}
                to={{ search: 'entry=header_badge' }}
              >
                <span className={css.navigationLinkContent}>
                  ❤ <FormattedMessage id="TopbarMobileMenu.savedItemsLink" />
                  <NotificationBadge className={css.notificationBadge} count={savedItemsCount} />
                  <span className={css.navigationArrow}>→</span>
                </span>
              </NamedLink>
            ) : null}
          </div>

          {/* Custom Links */}
          {extraLinks && extraLinks.length > 0 ? (
            <>
              <div className={css.sectionHeader}>
                <FormattedMessage id="TopbarMobileMenu.moreHeader" />
              </div>
              <div className={css.customLinksWrapper}>{extraLinks}</div>
            </>
          ) : null}

          <div className={css.spacer} />
        </div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage">
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        {/* Browse Section */}
        <div className={css.sectionHeader}>
          <FormattedMessage id="TopbarMobileMenu.browseSection" />
        </div>
        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('BrandsPage'))}
            name="BrandsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.allBrandsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CategoriesPage'))}
            name="CategoriesPage"
          >
            <FormattedMessage id="TopbarMobileMenu.allCategoriesLink" />
          </NamedLink>
          <Link to="/categories/Baby-Kids" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.babyKidsLink" />
          </Link>
          <Link to="/categories/Fashion" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.fashionLink" />
          </Link>
          <Link to="/categories/Home-Kitchen" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.homeKitchenLink" />
          </Link>
          <Link to="/categories/Jewelry-Accessories" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.jewelryLink" defaultMessage="Jewelry & Accessories" />
          </Link>
          <Link to="/categories/Beauty-Wellness" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.beautyLink" defaultMessage="Beauty & Wellness" />
          </Link>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('GiftsPage'))}
            name="GiftsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.giftsLink" defaultMessage="Gifts" />
          </NamedLink>
          {savedItemsCount > 0 ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SavedPage'))}
              name="SavedPage"
              to={{ search: 'entry=header_badge' }}
            >
              ❤ <FormattedMessage id="TopbarMobileMenu.savedItemsLink" />
              <NotificationBadge className={css.notificationBadge} count={savedItemsCount} />
            </NamedLink>
          ) : null}
        </div>

        {/* Account Section */}
        <div className={css.sectionHeader}>
          <FormattedMessage id="TopbarMobileMenu.accountSection" />
        </div>
        <ul className={css.accountLinksWrapper}>
          <li className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}>
            <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>
          </li>
          {manageListingsLinkMaybe}
          <li className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}>
            <NamedLink name="ProfileSettingsPage">
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          </li>
          <li className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}>
            <NamedLink name="AccountSettingsPage">
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
          </li>
        </ul>
        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
